<template>
	<div class="sidebar-tags" ref="sidebarTextInput">
		<div class="tags">
			<b-badge v-for="(tag, index) in data.tags"
			         :key="tag.text"
			         class="tag mb-2"
			         variant="primary">
			    <span class="text">{{ tag.text }} <span class="tag badge badge-pill badge-info">{{ tag.s_attribut }}</span> <span class="tag badge badge-pill badge-primary" v-bind:style="{ 'background-color' :  tag.color }" >{{ tag.s_type }}</span></span>
				<span class="remove" @click="handleTagRemove(index)" >&times;</span>
			</b-badge>
		</div>
		<div class="row" >
			<div class="col-12">
				<erco-text-input class="tagInput"
				                 :text="tagInputText"
				                 ref="inputField"
				                 @input="handleChange">
				</erco-text-input>
			</div>
		</div>

		<transition name="suggestions">
			<div class="suggestions-wrapper"  @click="handleSuggestionClick" >
				<p class="suggestions-title">Vorschläge</p>
				<div class="spinner-container">
					<erco-spinner v-if="loading" :is-fixed-color="true">
					</erco-spinner>
				</div>
				<b-list-group class="suggestions"
				              v-if="suggestions">
					<div v-for="suggestion, index in suggestions">
						<b-list-group-item  :key="suggestion"
					                   :action="true"
					                   :data-index="index">
							<span class="mr-2">
								{{suggestion.S_Value}}
							</span>
						 	<span class="tag badge badge-pill badge-info">{{suggestion.S_Attribut}}</span>
						 	<span class="tag badge badge-pill badge-primary" v-bind:style="{ 'background-color' :  suggestion.color }">{{suggestion.S_Type}}</span>
						</b-list-group-item>

					</div>
				</b-list-group>
				<div class="info_" v-if="suggestions && suggestions.length === 1">
					<span class="_text">Keine Vorschläge gefunden</span>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
	import ErcoSpinner from '@/components/atoms/ErcoSpinner';
	import ErcoTextInput from '@/components/molecules/ErcoTextInput';
	import StringToColor from '@/helper/colorHelper';

	function focusFirstElement() {
		const containerNode = this.$refs.inputField.$el;
		const firstInputNode = containerNode.querySelector('input');

		if (firstInputNode) {
			firstInputNode.focus();
		}
	}

	export default {
		name: 'ErcoSidebarTextInputMolecule',

		props: {
			data: {
				type: Object,
				default: { tags: [] },
			},
		},

		data() {
			return {
				tagInputText: '',
				timeoutHandler: null,
				suggestions: null,
				loading: false,
			};
		},

		computed: {
			atomarTags() {
				const atomarTags = {};
				this.data.tags.forEach((tag) => { atomarTags[tag.text] = tag.s_attribut; });
				return atomarTags;
			},
		},

		mounted: focusFirstElement,
		updated: focusFirstElement,

		methods: {
			handleChange(value) {
				clearTimeout(this.timeoutHandler);
				this.tagInputText = value;

				this.timeoutHandler = setTimeout(() => {
					const trimmedValue = this.tagInputText;

					this.suggestions = null;

					if (trimmedValue.length === 0) {
						return;
					}

					this.loading = true;

					this.$store.dispatch(this.data.suggestionAction, trimmedValue)
						.then(() => {
							this.suggestions =
								this.removeSelectedTagsFromSuggestions(
									this.$store.state.tagger.suggestions.suggestions);
							this.loading = false;
						})
						.catch(() => {
							this.loading = false;
						});
				}, 500);
			},

			handleTagRemove(index) {
				const tag = this.data.tags[index];

				const newSidebarContext = Object.assign({}, this.$store.state.sidebar.context);
				newSidebarContext.value.tags[tag.text] = {
					id: tag.id,
					value: tag.text,
					action: 'remove',
				};

				const newOutputData = Object.assign({}, this.$store.state.sidebar.outputData);
				newOutputData.tags.splice(index, 1);

				this.$store.dispatch('updateSidebar', {
					context: newSidebarContext,
					outputData: newOutputData,
				});
			},

			handleSuggestionClick(event) {
				const index = parseInt(event.target.getAttribute('data-index'), 10);
				this.addNewTag(this.suggestions[index]);
			},

			addNewTag(value) {
				const newSidebarOutputData = Object.assign({}, this.data);
				const newSidebarValue = Object.assign({}, this.$store.state.sidebar.context);

				newSidebarOutputData.tags.push({
					id: null,
					text: value.S_Value,
					s_attribut: value.S_Attribut,
					s_type: value.S_Type,
					color: StringToColor.colorstring(value.S_Type),
				});

				newSidebarValue.value.tags = (newSidebarValue.value.tags || {});
				newSidebarValue.value.tags[Object.values(value)[0]] = {
					value,
					action: 'add',
				};

				this.$store.dispatch('updateSidebar', {
					context: newSidebarValue,
					outputData: newSidebarOutputData,
				});

				this.tagInputText = '';
				this.suggestions = null;
				this.$refs.inputField.$el.focus();
			},

			removeSelectedTagsFromSuggestions(suggestions) {
				return suggestions.filter(suggestion => !(Object.values(suggestion)[0] in this.atomarTags));
			},
		},

		components: {
			ErcoSpinner,
			ErcoTextInput,
		},
	};
</script>

<style lang="scss">

	.sidebar-tags {
		padding: 0.5rem;
		padding-top: 1.5rem;

		& > .tags {
			min-height: 2.5rem;

			& > .tag {
				font-size: 1rem;
				padding: 0.5rem;
				margin-right: 0.5rem;

				& > .text {
					border-right: 1px solid rgba(255, 255, 255, 0.2);
					padding-right: 0.35rem;
				}

				& > .remove {
					cursor: pointer;
					vertical-align: text-top;
				}
			}
		}

		.suggestions-enter-active, .suggestions-leave-active {
		  transition: opacity 0.5s;
		}

		.suggestions-enter, .suggestions-leave-to {
			opacity: 0;
		}

		.suggestions-wrapper {
			& > .suggestions-title {
				font-weight: bold;
			}

			& > .spinner-container {
				text-align: center;

				& > .spinner {
					display: inline-block;
					width: 30px;
					text-align: center;
					margin: 1.25rem auto 1rem;
				}
			}

			& > .info_ {
				height: 3rem;
			}
		}
	}

</style>



// WEBPACK FOOTER //
// Tags.vue?097775c5